/**
 * Performance Monitoring Service
 * Tracks Core Web Vitals and custom performance metrics
 */

import { onCLS, onFCP, onLCP, onTTFB, onINP, Metric } from 'web-vitals';
import env from '../config/env';

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
  metadata?: Record<string, any>;
}

interface ResourceTiming {
  name: string;
  duration: number;
  size: number;
  type: string;
  cached: boolean;
}

interface PerformanceReport {
  metrics: PerformanceMetric[];
  resources: ResourceTiming[];
  memory?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  };
  navigation: {
    type: string;
    redirectCount: number;
    domContentLoaded: number;
    loadComplete: number;
  };
  userAgent: string;
  url: string;
  timestamp: number;
  sessionId: string;
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetric> = new Map();
  private buffer: PerformanceReport[] = [];
  private sessionId: string;
  private reportingEndpoint: string;
  private reportingInterval = 30000; // 30 seconds
  private maxBufferSize = 50;
  private isEnabled = true;
  private observers: Map<string, PerformanceObserver> = new Map();

  constructor() {
    this.sessionId = this.generateSessionId();
    // Use the API_URL from env config which already handles dev/prod
    // env.API_URL is 'http://localhost:3001/api' in development
    const apiUrl = env.API_URL || 'http://localhost:3001/api';
    this.reportingEndpoint = `${apiUrl}/analytics/performance`;
    
    this.initializeMonitoring();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeMonitoring() {
    if (!this.isEnabled || typeof window === 'undefined') return;

    // Core Web Vitals
    this.initializeCoreWebVitals();

    // Custom performance observers
    this.initializeResourceObserver();
    this.initializeLongTaskObserver();
    this.initializeNavigationObserver();

    // Memory monitoring (if available)
    this.initializeMemoryMonitoring();

    // Error tracking
    this.initializeErrorTracking();

    // Periodic reporting
    this.startPeriodicReporting();

    // Report on page unload
    this.setupUnloadReporting();
  }

  private initializeCoreWebVitals() {
    // Largest Contentful Paint
    onLCP((metric) => this.recordWebVital('LCP', metric));
    
    // Interaction to Next Paint (replaces FID in web-vitals v5)
    onINP((metric) => this.recordWebVital('INP', metric));
    
    // Cumulative Layout Shift
    onCLS((metric) => this.recordWebVital('CLS', metric));
    
    // First Contentful Paint
    onFCP((metric) => this.recordWebVital('FCP', metric));
    
    // Time to First Byte
    onTTFB((metric) => this.recordWebVital('TTFB', metric));
  }

  private recordWebVital(name: string, metric: Metric) {
    const performanceMetric: PerformanceMetric = {
      name,
      value: metric.value,
      rating: metric.rating || this.getRating(name, metric.value),
      timestamp: Date.now(),
      metadata: {
        id: metric.id,
        navigationType: metric.navigationType,
        delta: metric.delta,
      }
    };

    this.metrics.set(name, performanceMetric);
    this.trackMetric(performanceMetric);
  }

  private getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
    const thresholds: Record<string, [number, number]> = {
      LCP: [2500, 4000],
      CLS: [0.1, 0.25],
      FCP: [1800, 3000],
      TTFB: [800, 1800],
      INP: [200, 500],
    };

    const [good, poor] = thresholds[name] || [0, Infinity];

    if (value <= good) return 'good';
    if (value <= poor) return 'needs-improvement';
    return 'poor';
  }

  private initializeResourceObserver() {
    if (!('PerformanceObserver' in window)) return;

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.entryType === 'resource') {
            this.trackResource(entry as PerformanceResourceTiming);
          }
        });
      });

      observer.observe({ entryTypes: ['resource'] });
      this.observers.set('resource', observer);
    } catch (error) {
      console.warn('Failed to initialize resource observer:', error);
    }
  }

  private initializeLongTaskObserver() {
    if (!('PerformanceObserver' in window)) return;

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.duration > 50) { // Long task threshold
            this.trackMetric({
              name: 'long-task',
              value: entry.duration,
              rating: entry.duration > 100 ? 'poor' : 'needs-improvement',
              timestamp: Date.now(),
              metadata: {
                startTime: entry.startTime,
                name: entry.name,
              }
            });
          }
        });
      });

      observer.observe({ entryTypes: ['longtask'] });
      this.observers.set('longtask', observer);
    } catch (error) {
      console.warn('Failed to initialize long task observer:', error);
    }
  }

  private initializeNavigationObserver() {
    if (!('PerformanceObserver' in window)) return;

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming;
            this.trackMetric({
              name: 'navigation',
              value: navEntry.loadEventEnd - navEntry.fetchStart,
              rating: this.getRating('navigation', navEntry.loadEventEnd - navEntry.fetchStart),
              timestamp: Date.now(),
              metadata: {
                domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.fetchStart,
                domInteractive: navEntry.domInteractive - navEntry.fetchStart,
                redirectCount: navEntry.redirectCount,
                type: navEntry.type,
              }
            });
          }
        });
      });

      observer.observe({ entryTypes: ['navigation'] });
      this.observers.set('navigation', observer);
    } catch (error) {
      console.warn('Failed to initialize navigation observer:', error);
    }
  }

  private initializeMemoryMonitoring() {
    if (!('memory' in performance)) return;

    setInterval(() => {
      const memory = (performance as any).memory;
      if (memory) {
        this.trackMetric({
          name: 'memory',
          value: memory.usedJSHeapSize,
          rating: this.getMemoryRating(memory.usedJSHeapSize),
          timestamp: Date.now(),
          metadata: {
            totalJSHeapSize: memory.totalJSHeapSize,
            jsHeapSizeLimit: memory.jsHeapSizeLimit,
            usage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
          }
        });
      }
    }, 10000); // Check every 10 seconds
  }

  private getMemoryRating(bytes: number): 'good' | 'needs-improvement' | 'poor' {
    const mb = bytes / (1024 * 1024);
    if (mb < 50) return 'good';
    if (mb < 100) return 'needs-improvement';
    return 'poor';
  }

  private initializeErrorTracking() {
    window.addEventListener('error', (event) => {
      this.trackMetric({
        name: 'js-error',
        value: 1,
        rating: 'poor',
        timestamp: Date.now(),
        metadata: {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack,
        }
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.trackMetric({
        name: 'unhandled-rejection',
        value: 1,
        rating: 'poor',
        timestamp: Date.now(),
        metadata: {
          reason: event.reason,
        }
      });
    });
  }

  private trackResource(entry: PerformanceResourceTiming) {
    const resource: ResourceTiming = {
      name: entry.name,
      duration: entry.duration,
      size: entry.transferSize || 0,
      type: this.getResourceType(entry.name),
      cached: entry.transferSize === 0 && entry.decodedBodySize > 0,
    };

    // Track slow resources
    if (resource.duration > 1000) {
      this.trackMetric({
        name: 'slow-resource',
        value: resource.duration,
        rating: resource.duration > 3000 ? 'poor' : 'needs-improvement',
        timestamp: Date.now(),
        metadata: resource,
      });
    }

    // Track large resources
    if (resource.size > 500000) { // 500KB
      this.trackMetric({
        name: 'large-resource',
        value: resource.size,
        rating: resource.size > 1000000 ? 'poor' : 'needs-improvement',
        timestamp: Date.now(),
        metadata: resource,
      });
    }
  }

  private getResourceType(url: string): string {
    const extension = url.split('.').pop()?.toLowerCase() || '';
    const typeMap: Record<string, string> = {
      js: 'script',
      css: 'stylesheet',
      jpg: 'image',
      jpeg: 'image',
      png: 'image',
      gif: 'image',
      svg: 'image',
      webp: 'image',
      woff: 'font',
      woff2: 'font',
      ttf: 'font',
      eot: 'font',
    };
    return typeMap[extension] || 'other';
  }

  private trackMetric(metric: PerformanceMetric) {
    // Store metric
    this.metrics.set(`${metric.name}-${Date.now()}`, metric);

    // Check performance budget
    this.checkPerformanceBudget(metric);

    // Add to buffer for reporting
    this.addToBuffer();
  }

  private checkPerformanceBudget(metric: PerformanceMetric) {
    const budgets: Record<string, number> = {
      LCP: 2500,
      CLS: 0.1,
      FCP: 1800,
      TTFB: 800,
      INP: 200,
      'memory': 256 * 1024 * 1024, // 256MB - more reasonable for modern web apps
    };

    const budget = budgets[metric.name];
    if (budget && metric.value > budget) {
      // Only log for non-memory metrics or if memory exceeds 256MB
      if (metric.name !== 'memory' || metric.value > 256 * 1024 * 1024) {
        console.warn(`Performance budget exceeded for ${metric.name}: ${metric.value} > ${budget}`);
      }
      
      // Trigger custom event for budget exceeded
      window.dispatchEvent(new CustomEvent('performance-budget-exceeded', {
        detail: { metric, budget }
      }));
    }
  }

  private addToBuffer() {
    const report = this.generateReport();
    this.buffer.push(report);

    // Trim buffer if too large
    if (this.buffer.length > this.maxBufferSize) {
      this.buffer.shift();
    }
  }

  private generateReport(): PerformanceReport {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const memory = (performance as any).memory;

    return {
      metrics: Array.from(this.metrics.values()),
      resources: this.getTopResources(),
      memory: memory ? {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
      } : undefined,
      navigation: {
        type: navigation?.type || 'unknown',
        redirectCount: navigation?.redirectCount || 0,
        domContentLoaded: navigation?.domContentLoadedEventEnd - navigation?.fetchStart || 0,
        loadComplete: navigation?.loadEventEnd - navigation?.fetchStart || 0,
      },
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: Date.now(),
      sessionId: this.sessionId,
    };
  }

  private getTopResources(limit = 10): ResourceTiming[] {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    return resources
      .map(r => ({
        name: r.name,
        duration: r.duration,
        size: r.transferSize || 0,
        type: this.getResourceType(r.name),
        cached: r.transferSize === 0 && r.decodedBodySize > 0,
      }))
      .sort((a, b) => b.duration - a.duration)
      .slice(0, limit);
  }

  private startPeriodicReporting() {
    setInterval(() => {
      if (this.buffer.length > 0) {
        this.sendReport();
      }
    }, this.reportingInterval);
  }

  private setupUnloadReporting() {
    ['pagehide', 'beforeunload', 'visibilitychange'].forEach(event => {
      window.addEventListener(event, () => {
        if (document.visibilityState === 'hidden' || event === 'beforeunload') {
          this.sendReport(true);
        }
      });
    });
  }

  private async sendReport(useBeacon = false) {
    if (this.buffer.length === 0) return;

    const reports = [...this.buffer];
    this.buffer = [];

    const payload = JSON.stringify({ reports });

    // Ensure endpoint is always absolute
    let endpoint = this.reportingEndpoint;
    if (!endpoint.startsWith('http')) {
      // If somehow the endpoint is relative, make it absolute
      const apiUrl = env.API_URL || 'http://localhost:3001/api';
      endpoint = `${apiUrl}/analytics/performance`;
      console.warn('Performance endpoint was relative, fixing to:', endpoint);
      this.reportingEndpoint = endpoint; // Update the stored endpoint
    }

    try {
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(endpoint, payload);
      } else {
        await fetch(endpoint, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include cookies for authentication
          body: payload,
        });
      }
    } catch (error) {
      console.error('Failed to send performance report:', error);
      // Re-add to buffer for retry
      this.buffer.unshift(...reports);
    }
  }

  // Public API
  public measureTime(name: string): () => void {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.trackMetric({
        name: `custom-${name}`,
        value: duration,
        rating: duration < 100 ? 'good' : duration < 1000 ? 'needs-improvement' : 'poor',
        timestamp: Date.now(),
      });
    };
  }

  public mark(name: string) {
    performance.mark(name);
  }

  public measure(name: string, startMark: string, endMark?: string) {
    try {
      const measure = performance.measure(name, startMark, endMark);
      this.trackMetric({
        name: `measure-${name}`,
        value: measure.duration,
        rating: measure.duration < 100 ? 'good' : measure.duration < 1000 ? 'needs-improvement' : 'poor',
        timestamp: Date.now(),
      });
    } catch (error) {
      console.error(`Failed to measure ${name}:`, error);
    }
  }

  public getMetrics(): PerformanceMetric[] {
    return Array.from(this.metrics.values());
  }

  public getReport(): PerformanceReport {
    return this.generateReport();
  }

  public enable() {
    this.isEnabled = true;
  }

  public disable() {
    this.isEnabled = false;
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
  }

  public setReportingEndpoint(endpoint: string) {
    this.reportingEndpoint = endpoint;
  }

  public setReportingInterval(interval: number) {
    this.reportingInterval = interval;
  }

  public flush() {
    return this.sendReport();
  }
}

// Create and export singleton instance
const performanceMonitorInstance = new PerformanceMonitor();
export default performanceMonitorInstance;